import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { formatBillingFrequency } from 'utils/MembershipUtils';
import Header from 'components/Checkout2/UI/Header';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import Footer from './Footer';
import Step from './Step';
import { Box, Typography } from '@material-ui/core';
import InfoAlert from './InfoAlert';
import Pet from '@spike/pet-model';
import { SellInfo } from './SellMembership';
import cloneDeep from 'lodash/cloneDeep';
import SelectMultiplePets from './SelectMultiplePets';
import PetInfo from 'components/OrderDetail/PetDetail/PetInfo';
import { Membership } from '@spike/membership-model';
import { SelectMembership } from './SelectMembership';
import Title from './Title';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import ConsumedPresetSection from './ConsumedPresetSection';
import Details from './Details';
import moment, { Moment } from 'moment-timezone';
import { useTimeZone } from 'hooks';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';

export interface EditStepProps {
    pets: Array<Pet>;
    sellInfo: SellInfo;
    memberships: Array<Membership>;
    onClose: () => void;
    onBack: () => void;
    onSave: (sellInfo: SellInfo) => void;
    onAddPets: () => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        addPetLink: {
            color: '#92B4A7',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            marginTop: 20
        },
        title: {
            marginTop: 30
        },
        selectMultiplePets: {
            marginTop: 15
        },
        petInfo: {
            marginTop: 30
        },
        membershipContainer: {
            borderTop: '1px solid #D4D4D4',
            marginTop: 30
        },
        selectMembership: {
            marginTop: 15
        },
        row: {
            display: 'flex',
            alignItems: 'center'
        },
        cell: {
            display: 'flex',
            alignItems: 'center'
        },
        icon: {
            marginRight: 7
        },
        type: {
            fontSize: 14,
            textTransform: 'capitalize'
        },
        typeContainer: {
            display: 'flex',
            alignItems: 'center',
            marginTop: 10
        },
        membershipTitleContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            width: '100%'
        },
        membershipInfoRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 25
        },
        membershipInfoColumn: {
            'flex': 1,
            '&:nth-child(2)': {
                flex: 1.5
            }
        },
        expandableSelector: {
            marginTop: 6
        },
        datesRow: {
            display: 'flex',
            gap: 25,
            marginTop: 0
        },
        dateColumn: {
            'flex': 1,
            '&:nth-child(2)': {
                flex: 1.5
            }
        },
        dateSelector: {
            width: 'auto',
            marginTop: 10
        },
        dateValue: {
            fontWeight: 500,
            marginTop: 10
        },
        helperText: {
            fontSize: 12,
            color: '#7A7A7A',
            marginTop: 5,
            lineHeight: 1.4
        },
        tooltipIcon: {
            fontSize: 16,
            color: '#5e8677',
            cursor: 'pointer',
            position: 'relative',
            top: 15,
            marginLeft: 5
        },
        titleWithTooltip: {
            display: 'flex',
            alignItems: 'center',
            gap: 3
        },
        customTooltip: {
            marginTop: 20
        }
    })
);

export const EditStep: FunctionComponent<EditStepProps> = props => {
    const classes = useStyles(props);

    const [editSellInfo, setEditSellInfo] = useState(cloneDeep(props.sellInfo));
    const selectedMembership = useMemo(
        () => props.memberships.find(membership => membership.id === editSellInfo.membershipId),
        [editSellInfo]
    );

    const timeZone = useTimeZone();
    const today = moment().tz(timeZone).startOf('day');

    const [startDate, setStartDate] = useState<Moment>(editSellInfo.startAt || today.clone());
    const [isStartDateSelectorExpanded, setIsStartDateSelectorExpanded] = useState(false);

    const [endDate, setEndDate] = useState<Moment | null>(editSellInfo.endAt || null);
    const [isEndDateSelectorExpanded, setIsEndDateSelectorExpanded] = useState(false);

    // Update startAt in editSellInfo when startDate changes
    useEffect(() => {
        setEditSellInfo(prev => ({ ...prev, startAt: startDate }));
    }, [startDate]);

    // Update endAt in editSellInfo when endDate changes
    useEffect(() => {
        setEditSellInfo(prev => ({ ...prev, endAt: endDate }));
    }, [endDate]);

    // Reset endDate if it becomes invalid when startDate changes
    useEffect(() => {
        if (endDate && endDate.isBefore(startDate, 'day')) {
            setEndDate(null);
        }
    }, [startDate]);

    // Auto-calculate and set endDate for packages with expiration
    useEffect(() => {
        if (selectedMembership?.enrollmentType === 'package' && selectedMembership?.packageExpiration === 'limited') {
            const defaultEndDate = calculateDefaultEndDate();
            if (defaultEndDate) {
                setEndDate(defaultEndDate);
            }
        }
    }, [selectedMembership?.id, startDate]);

    // Calculate maximum allowed end date based on membership type and expiration rules
    const calculateMaxEndDate = (): Moment | undefined => {
        if (!selectedMembership) return undefined;

        // Subscriptions: no limit
        if (selectedMembership.enrollmentType === 'subscription') {
            return undefined;
        }

        // Packages "Never Expires": no limit
        if (selectedMembership.packageExpiration === 'never' || !selectedMembership.packageExpiration) {
            return undefined;
        }

        // Packages with expiration: limit to start + expiration duration
        if (
            selectedMembership.packageExpiration === 'limited' &&
            selectedMembership.packageExpirationValue &&
            selectedMembership.packageExpirationUnit
        ) {
            return startDate
                .clone()
                .add(
                    selectedMembership.packageExpirationValue,
                    selectedMembership.packageExpirationUnit as moment.unitOfTime.DurationConstructor
                );
        }

        return undefined;
    };

    // Calculate default end date for packages with expiration
    const calculateDefaultEndDate = (): Moment | null => {
        if (!selectedMembership) return null;

        // Only for packages with limited expiration
        if (
            selectedMembership.enrollmentType === 'package' &&
            selectedMembership.packageExpiration === 'limited' &&
            selectedMembership.packageExpirationValue &&
            selectedMembership.packageExpirationUnit
        ) {
            return startDate
                .clone()
                .add(
                    selectedMembership.packageExpirationValue,
                    selectedMembership.packageExpirationUnit as moment.unitOfTime.DurationConstructor
                );
        }

        return null;
    };

    const startDateChangeHandler = (date: Moment | null) => {
        if (date) {
            setStartDate(date);
            setIsStartDateSelectorExpanded(false);
        }
    };

    const endDateChangeHandler = (date: Moment | null) => {
        // Allow clearing the field (for subscriptions "Until Canceled")
        if (!date) {
            setEndDate(null);
            setIsEndDateSelectorExpanded(false);
            return;
        }

        // Validation: end date >= start date
        if (date.isBefore(startDate, 'day')) {
            // Date picker already restricts this with minDate
            return;
        }

        const maxDate = calculateMaxEndDate();

        // Validation: end date <= maxDate (for packages with expiration)
        if (maxDate && date.isAfter(maxDate, 'day')) {
            // Date picker already restricts this with maxDate
            return;
        }

        setEndDate(date);
        setIsEndDateSelectorExpanded(false);
    };

    const hasPets = props.pets.length > 0;

    const alertMessage = hasPets ? 'All selected pets are eligible to use the membership.' : 'This client has no pets.';

    const addPetLink = (
        <Box onClick={props.onAddPets}>
            <Typography className={classes.addPetLink}>+ Add Pet</Typography>
        </Box>
    );

    const selectedPets = useMemo(
        () => props.pets.filter(pet => editSellInfo.petIds.includes(pet.id ?? 0)),
        [props.pets, editSellInfo]
    );

    const deletePet = (petId: number) =>
        setEditSellInfo(prev => ({ ...prev, petIds: prev.petIds.filter(id => id !== petId) }));

    const selectMembershipHandler = (membershipId: number) => {
        setEditSellInfo(prev => ({ ...prev, membershipId, consumedPreset: undefined }));
    };

    const header = (
        <Header
            title="Select Pet(s) for Membership"
            onClose={props.onClose}
            onBack={props.onBack}
        />
    );

    const body = (
        <>
            <InfoAlert>{alertMessage}</InfoAlert>
            {!hasPets ? (
                addPetLink
            ) : (
                <>
                    <Title className={classes.title}>Pets</Title>
                    <SelectMultiplePets
                        pets={props.pets}
                        selectedPetIds={editSellInfo.petIds}
                        onSelectionChange={petIds => setEditSellInfo(prev => ({ ...prev, petIds: [...petIds] }))}
                        className={classes.selectMultiplePets}
                    />
                    <Box>
                        {selectedPets.map(pet => (
                            <PetInfo
                                key={pet.id}
                                pet={pet}
                                onDelete={() => deletePet(pet.id ?? 0)}
                                className={classes.petInfo}
                            />
                        ))}
                    </Box>
                    {selectedPets.length > 0 && (
                        <Box className={classes.membershipContainer}>
                            <Box className={classes.membershipTitleContainer}>
                                <Title className={classes.title}>Membership</Title>
                                {editSellInfo.membershipId && <Details membership={selectedMembership} />}
                            </Box>
                            <SelectMembership
                                memberships={props.memberships}
                                selectedMembershipId={editSellInfo.membershipId}
                                onSelect={selectMembershipHandler}
                                placeholder="Select a Membership"
                                className={classes.selectMembership}
                            />
                        </Box>
                    )}
                    {editSellInfo.membershipId && (
                        <Box className={classes.membershipInfoRow}>
                            <Box className={classes.membershipInfoColumn}>
                                <Title className={classes.title}>Membership Type</Title>
                                <Box className={classes.typeContainer}>
                                    <FontAwesomeIcon
                                        icon={
                                            selectedMembership?.enrollmentType === 'package'
                                                ? faMoneyBill
                                                : faArrowsRotate
                                        }
                                        className={classes.icon}
                                    />
                                    <Typography className={classes.type}>
                                        {selectedMembership?.enrollmentType}
                                    </Typography>
                                </Box>
                            </Box>
                            {selectedMembership?.enrollmentType === 'subscription' && (
                                <Box className={classes.membershipInfoColumn}>
                                    <Title className={classes.title}>Billing Frequency</Title>
                                    <Box className={classes.typeContainer}>
                                        <Typography className={classes.type}>
                                            {(selectedMembership && formatBillingFrequency(selectedMembership)) || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                    {/* Start Date and End Date */}
                    {editSellInfo.membershipId && (
                        <Box className={classes.datesRow}>
                            <Box className={classes.dateColumn}>
                                <Title className={classes.title}>Start Membership Date</Title>
                                <ExpandableSelector
                                    title=""
                                    id="start-date-selector"
                                    expanded={isStartDateSelectorExpanded}
                                    childrenClassName={classes.dateSelector}
                                    label={startDate.format('MMM DD, YYYY')}
                                    onExpand={() => setIsStartDateSelectorExpanded(!isStartDateSelectorExpanded)}
                                    onClickAway={() => setIsStartDateSelectorExpanded(false)}
                                    className={classes.expandableSelector}
                                >
                                    <DatePicker
                                        minDate={today}
                                        maxDate={undefined}
                                        timeZone={timeZone}
                                        value={startDate}
                                        onChange={startDateChangeHandler}
                                    />
                                </ExpandableSelector>
                            </Box>

                            <Box className={classes.dateColumn}>
                                <Box className={classes.titleWithTooltip}>
                                    <Title className={classes.title}>End Date</Title>
                                    <Tooltip
                                        className={classes.customTooltip}
                                        content={
                                            selectedMembership?.enrollmentType === 'package'
                                                ? 'Date when this package expires and benefits can no longer be used.'
                                                : 'Date when billing and membership benefits will stop.'
                                        }
                                        placement="top"
                                    >
                                        <div>
                                            <FontAwesomeIcon
                                                icon={faInfoCircle}
                                                className={classes.tooltipIcon}
                                            />
                                        </div>
                                    </Tooltip>
                                </Box>
                                <ExpandableSelector
                                    title=""
                                    id="end-date-selector"
                                    expanded={isEndDateSelectorExpanded}
                                    childrenClassName={classes.dateSelector}
                                    label={
                                        endDate
                                            ? endDate.format('MMM DD, YYYY')
                                            : selectedMembership?.enrollmentType === 'subscription' ||
                                              selectedMembership?.packageExpiration === 'never' ||
                                              !selectedMembership?.packageExpiration
                                            ? 'Until Canceled'
                                            : 'Optional'
                                    }
                                    onExpand={() => setIsEndDateSelectorExpanded(!isEndDateSelectorExpanded)}
                                    onClickAway={() => setIsEndDateSelectorExpanded(false)}
                                    className={classes.expandableSelector}
                                    dropdownAlign={'right'}
                                >
                                    <DatePicker
                                        minDate={startDate}
                                        maxDate={calculateMaxEndDate()}
                                        timeZone={timeZone}
                                        value={endDate}
                                        onChange={endDateChangeHandler}
                                    />
                                </ExpandableSelector>
                            </Box>
                        </Box>
                    )}
                    {/* Prior usage - only for packages with finite items */}
                    {editSellInfo.membershipId &&
                        selectedMembership?.enrollmentType === 'package' &&
                        selectedMembership.membershipItems.filter(i => !i.unlimited && i.id !== undefined).length >
                            0 && (
                            <ConsumedPresetSection
                                membershipItems={selectedMembership.membershipItems.filter(
                                    i => !i.unlimited && i.id !== undefined
                                )}
                                consumedPreset={editSellInfo.consumedPreset}
                                onChange={preset => setEditSellInfo(prev => ({ ...prev, consumedPreset: preset }))}
                            />
                        )}
                </>
            )}
        </>
    );

    const footer = (
        <Footer
            total={Number(selectedMembership?.total ?? 0)}
            buttonLabel="Save"
            disableButton={editSellInfo.petIds.length === 0 || editSellInfo.membershipId === undefined}
            onClickButton={() => props.onSave(editSellInfo)}
        />
    );

    return (
        <Step
            header={header}
            body={body}
            footer={footer}
        />
    );
};

export default EditStep;
